import React, { useState } from 'react';
import { IioTable } from '../../components/IioTable';
import { useProject } from '../../hooks/useProject';
import { useProjectUsers, useUpdateUserRole, useRemoveUser, type ProjectAccount } from '../../hooks/useProjectUsers';
import { useInvitations, useDeleteInvitation } from '../../hooks/useInvitations';
import { useProjectRoles } from '../../hooks/useProjectRoles';
import { InviteUserModal } from './InviteUserModal';
import { RequestReviewerModal } from './RequestReviewerModal';
import { useCurrentUserEmail } from '../../hooks/useCurrentUserEmail';
import { FilterInput } from '../../components/FilterInput';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import { SortableTh } from '../../components/SortableTh';
import { compareBy, type SortDir } from '../../utilities/sort';

type UserSortKey = 'fullName' | 'email' | 'role';
type InviteSortKey = 'email' | 'role';

export function UsersTab({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  const { data: users = [], isLoading: loadingUsers } = useProjectUsers(projectId);
  const { data: invitations = [], isLoading: loadingInvites } = useInvitations(projectId);
  const { data: roles = [] } = useProjectRoles(projectId);
  const updateRole = useUpdateUserRole(projectId);
  const removeUser = useRemoveUser(projectId, project?.name);
  const deleteInvite = useDeleteInvitation(projectId);

  const [roleSelections, setRoleSelections] = useState<Record<string, string>>({});
  const [filterText, setFilterText] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showReviewerModal, setShowReviewerModal] = useState(false);
  const [userSortKey, setUserSortKey] = useState<UserSortKey>('fullName');
  const [userSortDir, setUserSortDir] = useState<SortDir>('asc');
  const [invSortKey, setInvSortKey] = useState<InviteSortKey>('email');
  const [invSortDir, setInvSortDir] = useState<SortDir>('asc');
  const myEmail = useCurrentUserEmail();

  const handleUserSort = (key: UserSortKey) => {
    if (key === userSortKey) setUserSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setUserSortKey(key); setUserSortDir('asc'); }
  };

  const handleInvSort = (key: InviteSortKey) => {
    if (key === invSortKey) setInvSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setInvSortKey(key); setInvSortDir('asc'); }
  };

  const getRoleId = (userId: string, fallback: string) =>
    roleSelections[userId] ?? fallback;

  const handleRoleChange = (accountId: string, roleId: string) => {
    setRoleSelections(prev => ({ ...prev, [accountId]: roleId }));
    const fullName = users.find(u => u.id === accountId)?.fullName ?? '';
    const roleName = roles.find(r => r.id === roleId)?.name ?? '';
    updateRole.mutate({ accountId, roleId, fullName, roleName });
  };

  const handleRemoveUser = (user: ProjectAccount) => {
    removeUser.mutate({ accountId: user.id, role: user.projectRole, fullName: user.fullName });
  };

  return (
    <div>
      <h2><i className="fas fa-users" aria-hidden="true" /> Project Users</h2>

      <div className="btn-toolbar">
        {project && (
          <button className="btn btn-primary" onClick={() => setShowInviteModal(true)} data-testid="admin-users-invite-btn">
            Invite User <i className="fas fa-plus-circle" aria-hidden="true" />
          </button>
        )}
        {project && (
          <button className="btn btn-primary" onClick={() => setShowReviewerModal(true)} data-testid="admin-users-reviewer-btn">
            On-Demand Reviewer <i className="fas fa-plus-circle" aria-hidden="true" />
          </button>
        )}
        <FilterInput value={filterText} onChange={setFilterText} testId="admin-users-filter-input" />
      </div>

      {loadingUsers ? <LoadingPlaceholder /> : users.length > 0 && (
        <IioTable data-testid="admin-users-table">
          <thead>
            <tr>
              <SortableTh label="Name" sortKey="fullName" current={userSortKey} dir={userSortDir} onSort={handleUserSort} />
              <SortableTh label="Email" sortKey="email" current={userSortKey} dir={userSortDir} onSort={handleUserSort} />
              <th>Status</th>
              <SortableTh label="Role" sortKey="role" current={userSortKey} dir={userSortDir} onSort={handleUserSort} />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {[...users].sort(compareBy(u => {
              if (userSortKey === 'fullName') return u.fullName;
              if (userSortKey === 'email') return u.email;
              return u.projectRole.name;
            }, userSortDir)).filter(u => {
              if (!filterText) return true;
              const q = filterText.toLowerCase();
              return u.fullName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
            }).map(u => (
              <tr key={u.id} className={u.email === myEmail ? 'active' : ''} data-testid="admin-users-row">
                <td>
                  {u.email === myEmail && (
                    <i className="fas fa-user" aria-hidden="true" title="Me" />
                  )}{' '}
                  {u.fullName}
                </td>
                <td>{u.email}</td>
                <td>Accepted</td>
                <td>
                  <select
                    className="form-control"
                    data-testid="admin-users-role-select"
                    value={getRoleId(u.id, u.projectRole.id)}
                    onChange={e => handleRoleChange(u.id, e.target.value)}
                  >
                    {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className="btn btn-xs btn-danger"
                    onClick={() => handleRemoveUser(u)}
                    data-testid="admin-users-remove-btn"
                  >
                    <i className="fas fa-trash" aria-hidden="true" />{' '}
                    Un-Invite{u.email === myEmail ? ' Myself' : ''}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </IioTable>
      )}

      {loadingInvites ? <LoadingPlaceholder /> : invitations.length > 0 && (
        <>
          <h3>Pending Invitations</h3>
          <IioTable data-testid="admin-users-invitation-table">
            <thead>
              <tr>
                <SortableTh label="Email" sortKey="email" current={invSortKey} dir={invSortDir} onSort={handleInvSort} />
                <th>Status</th>
                <SortableTh label="Role" sortKey="role" current={invSortKey} dir={invSortDir} onSort={handleInvSort} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...invitations].sort(compareBy(inv => {
                if (invSortKey === 'email') return inv.email;
                return inv.role.name;
              }, invSortDir)).map(inv => (
                <tr key={inv.id} className="info" data-testid="admin-users-invitation-row">
                  <td>{inv.email}</td>
                  <td>Invited</td>
                  <td>{inv.role.name}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn-xs btn-danger"
                      onClick={() => deleteInvite.mutate(inv.id)}
                      data-testid="admin-users-invitation-remove-btn"
                    >
                      <i className="fas fa-trash" aria-hidden="true" />{' '}
                      Un-Invite{inv.email === myEmail ? ' Myself' : ''}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </IioTable>
        </>
      )}

      {showInviteModal && project && (
        <InviteUserModal
          projectId={projectId}
          projectName={project.name}
          onClose={() => setShowInviteModal(false)}
        />
      )}
      {showReviewerModal && project && (
        <RequestReviewerModal
          projectId={projectId}
          projectName={project.name}
          onClose={() => setShowReviewerModal(false)}
        />
      )}
    </div>
  );
}
